import { type TimeRangeBoundedQueue } from '@game-libs/exc-libs/src/data-structures';

export enum DamageSource {
  Click = 'click',
  Tick = 'tick',
}

export enum CharacterClass {
  Warrior = 'warrior',
  Wizard = 'wizard',
  Elf = 'elf',
  Thief = 'thief',
  Cleric = 'cleric',
  Bard = 'bard',
  Lumberjack = 'lumberjack',
  Farmer = 'farmer',
  Miner = 'miner',
  Catapult = 'catapult',
}

export enum BuildingClass {
  Garrison = 'garrison',
  Outpost = 'outpost',
  Academy = 'academy',
  Guild = 'guild',
  Troupe = 'troupe',
  Seminary = 'seminary',
}

export enum HubClass {
  MilitaryCouncil = 'council',
  WizardNexus = 'nexus',
  Forest = 'forest',
  ShadowCongress = 'congress',
  Theater = 'theater',
  College = 'college',
}

export enum UpgradeType {
  // CLICK
  PerfectClick = 'perfectClick',
  CriticalStrike = 'criticalStrike',
  FeatherFinger = 'featherFinger',
  CarpalCure = 'carpalCure',
  IronFinger = 'ironFinger',
  WakeupCall = 'wakeupCall',
  GoldenExplosion = 'goldenExplosion',
  NaturalLeader = 'naturalLeader',
  MoneyCursor = 'moneyCursor',

  // WARRIOR
  ShortSword = 'shortSword',
  LongSword = 'longSword',
  TwoHandedSword = 'twoHandedSword',
  BattleShout = 'battleShout',
  WarCry = 'warCry',
  Barbarian = 'barbarian',
  ThickArmor = 'thickArmor',
  FireArmor = 'fireArmor',
  SilverBlade = 'silverBlade',
  GoldenBlade = 'goldenBlade',
  LoyalMercenaries = 'loyalMercenaries',
  LoyalServants = 'loyalServants',
  TeamWork = 'teamWork',

  // WIZARD
  MagicMissile = 'magicMissile',
  ManaSword = 'manaSword',
  WeatherForecast = 'weatherForecast',
  LightningStrike = 'lightningStrike',
  MagicMouse = 'magicMouse',
  ManaSurge = 'manaSurge',
  ManaPool = 'manaPool',
  ManaBoost = 'manaBoost',
  SilverStaff = 'silverStaff',
  GoldenStaff = 'goldenStaff',
  Archimage = 'archimage',

  // ELF
  HuntersEye = 'huntersEye', // increases accuracy/crit for bird
  CriticalDamage = 'criticalDamage',
  CriticalChance = 'criticalChance',
  HuntingSeason = 'huntingSeason',
  MultipleShot = 'multipleShot',
  SilverArrow = 'silverArrow',
  GoldenArrow = 'goldenArrow',
  IceArrow = 'iceArrow',

  // THIEF
  SharpDagger = 'sharpDagger',
  PoisonDagger = 'poisonDagger',
  BlackMamba = 'blackMamba', // Poison damage
  SmokeBomb = 'smokeBomb',
  LingeringToxin = 'lingeringToxin', // Poison duration increases per purchase
  Pickpocket = 'pickpocket',
  MidasTouch = 'midasTouch',
  FastHands = 'fastHands',

  // BARD
  LuteSolo = 'luteSolo',
  TuningFork = 'tuningFork',
  LockerRoomSpeech = 'lockerRoomSpeech',
  Encore = 'encore',
  ObnoxiousGuitarist = 'obnoxiousGuitarist',
  PiercedEardrums = 'piercedEardrums',

  // CLERIC
  HeroResources = 'heroResources',
  BlessedAura = 'blessedAura',
  RecruitWarriors = 'recruitWarriors',
  RecruitWizards = 'recruitWizards',
  RecruitElves = 'recruitElves',
  RecruitThieves = 'recruitThieves',
  RecruitBards = 'recruitBards',
  RecruitClerics = 'recruitClerics',

  // CATAPULT
  Cats = 'cats',
}

export type GeneratorClass = CharacterClass | BuildingClass | HubClass;

export type Resource = 'gold' | 'food' | 'wood' | 'ore';

export type ResourceCost = {
  [key in Resource]?: number;
};

export type Purchasable = {
  id: GeneratorClass | UpgradeType;
  cost: ResourceCost;
  costGrowthRate: number;
  isUnlocked: boolean;
  purchased: number;
  purchaseLimit?: number;
  type: UnitType;
};

export type Generator = Omit<Purchasable, 'id'> & {
  id: GeneratorClass;
  owned: number;
  level: number;
  baseGeneration: number; // Number of items generated, damage given or gold generated
  ticksToGenerate?: number; // Ticks to wait until generate next item
};

export type Upgrade = Omit<Purchasable, 'id'> & {
  id: UpgradeType;
  icon: string[];
  tier: 'click' | 'generators' | 'items';
  multiplier: number;
  bonusPerOwned: number;
  purchaseLimit?: number;
  status?: UpgradeStatus;
  isLockedOnDemo?: boolean;
};

export enum UpgradeStatus {
  Hidden = 'hidden',
  Locked = 'locked',
  Unlocked = 'unlocked',
}

export enum UnitType {
  TROOPS = 'troops',
  SUPPORT = 'support',
  RESOURCES = 'resources',
  ENGINEERING = 'engineering',
  UPGRADE = 'upgrade',
}

export enum GenerationType {
  Damage = 'damage',
  Unit = 'unit',
  Building = 'building',
  Inspiration = 'inspiration',
  Gold = 'gold',
  Food = 'food',
  Ore = 'ore',
  Wood = 'wood',
}

// export enum GeneratorType {
//   Unit = 'unit',
//   Building = 'building',
//   Tower = 'tower',
//   Gold = 'gold',
// }

export type UpgradeConfigKey = Partial<GeneratorType> | 'click' | 'bird';

export type EntityUpgrades = Record<UpgradeType | 'string', Upgrade>;
export type EntityUpgradesList = Record<string, EntityUpgrades>;

export type GameGenerators = {
  // Troops
  warrior: Generator;
  wizard: Generator;
  elf: Generator;
  garrison: Generator;
  academy: Generator;
  outpost: Generator;
  council: Generator; // Legion???
  nexus: Generator;
  forest: Generator;

  // Support
  thief: Generator;
  bard: Generator;
  cleric: Generator;
  guild: Generator;
  troupe: Generator;
  seminary: Generator;
  theater: Generator;
  congress: Generator;
  college: Generator; // College of Cardinals

  // Engineering
  catapult: Generator;
  // builder: Generator;
  // blacksmith: Generator;

  // Resources
  farmer: Generator;
  lumberjack: Generator;
  miner: Generator;
  // treasurer: Generator;
};

export type GeneratorType = keyof GameGenerators;

export type StatisticsQueue = {
  gold: TimeRangeBoundedQueue<number>;
  damage: TimeRangeBoundedQueue<number>;
};

// Creating a type from AdditionalGameState, but the values are not Signals
/**
 * React context value for `useReducer` — intentionally empty. Gameplay slices live in Zustand
 * (`useGeneratorsStore`, `useUpgradesStore`, currency, etc.); the context stays stable for dispatch-only subscribers.
 */
export type GUIGameState = Record<string, never>;

/**
 * Canonical empty value for `GUIGameState` (save load default, reducer reset, `useReducer` seed).
 * Gameplay slices live in Zustand; this stays a stable `{}` so dispatch-only subscribers rarely re-render.
 */
export const EMPTY_GUI_GAME_STATE: GUIGameState = {};

export enum UpgradeActions {
  BUY_UPGRADE = 'buy_upgrade',
  SHOW_UPGRADE = 'show_upgrade',
  UNLOCK_UPGRADE = 'unlock_upgrade',
}

export enum GUIActions {
  START_LEVEL = 'start_level',
  CHANGE_LEVEL = 'change_level',
  TICK = 'tick',
  SET_GOLD = 'set_gold',
  GENERATE_GENERATORS = 'generate_generators',
  HEAL_DRAGON = 'heal_dragon',
  POISON_DRAGON = 'poison_dragon',
  ADD_STUN_EFFECT = 'add_stun_effect',
  CLICK_DRAGON = 'click_dragon',
  CLICK_FARM = 'click_farm',
  CLICK_BIRD = 'click_bird',
  BUY_GENERATOR = 'buy_generator',
  REMOVE_GENERATORS = 'remove_generators',
  ADD_GENERATORS = 'add_generators',
  UNLOCK_TIER = 'unlock_tier',
  HOVER_ITEM = 'hover_item',
  NOT_ENOUGH_MANA = 'not_enough_mana',
  ENOUGH_MANA = 'enough_mana',
  OPEN_SETTINGS = 'open_settings',
  MET_DRAGON = 'met_dragon',
  TOGGLE_BUY_MAX = 'toggle_buy_max',
  TOGGLE_UI = 'toggle_ui',
  UNLOCK_ALL = 'unlock_all',
  CHEATS = 'cheats',
  INIT_GAME = 'init_game',
  RESET_STATE = 'reset_state',
  WIN_GAME = 'win_game',
  CLEAR_SAVE = 'clear_save',
  ACTIVATE_INSPIRATION = 'activate_inspiration',
  DUMMY_KILLED = 'dummy_killed',
  OPEN_DUNGEON_CRAWLER = 'open_dungeon_crawler',
  CLOSE_DUNGEON_CRAWLER = 'close_dungeon_crawler',
  ADD_DUNGEON_REWARDS = 'add_dungeon_rewards',
  CONSUME_DUNGEON_KEY = 'consume_dungeon_key',
  SHOWN_FIRST_MANA_OUT_DIALOG = 'shown_first_mana_out_dialog',
}

export type Statistics = {
  dps: number;
  timeElapsed: number;
  numberOfClicks: number;
};

export const getGenerationType = (generatorClass: GeneratorClass) => {
  switch (generatorClass) {
    case CharacterClass.Warrior:
    case CharacterClass.Wizard:
    case CharacterClass.Elf:
      return GenerationType.Damage;
    case CharacterClass.Thief:
      return GenerationType.Gold;
    case BuildingClass.Garrison:
    case BuildingClass.Academy:
    case BuildingClass.Outpost:
    case BuildingClass.Guild:
    case BuildingClass.Troupe:
    case BuildingClass.Seminary:
    case CharacterClass.Cleric:
      return GenerationType.Unit;
    case HubClass.MilitaryCouncil:
    case HubClass.WizardNexus:
    case HubClass.Forest:
    case HubClass.ShadowCongress:
    case HubClass.Theater:
    case HubClass.College:
      return GenerationType.Building;
    case CharacterClass.Bard:
      return GenerationType.Inspiration;

    default:
      return GenerationType.Gold;
  }
};
